import { c } from 'ttag';

import { AccountUsersQuery } from '../../graphql/__generated__/types';

export type AccountUserRow = NonNullable<NonNullable<AccountUsersQuery['accountUsers']>['items']>[number];

/**
 * Reading a row of « une personne par ligne » (PCG-90).
 *
 * The list used to render **one row per membership**, so somebody attached to three
 * accounts appeared three times and the counter above said « N utilisateurs » while
 * counting attachments: 33 % too many on a realistic set. The API now returns the
 * person, carries the aggregate, and carries the detail alongside it; these helpers
 * are what turns that into cells.
 */

/**
 * The accounts, as « Verlaine Energie +2 ».
 *
 * **Attention, the surplus counts the accounts YOU can see, not the ones the person has.**
 * `memberships` is bounded by the actor's perimeter: aggregating must not widen
 * anything, so two operators with different portfolios legitimately read different
 * surpluses for the same person. Nothing in the data says this, and two operators
 * comparing screens would conclude the base is inconsistent: the column header
 * carries the warning, and the tooltip below names the accounts so the reader sees
 * *which* ones are being counted rather than only how many.
 */
export const accountsCell = (
    user: AccountUserRow
): { first: string; surplus: number; othersTitle: string | undefined } => {
    const names = (user.memberships ?? [])
        .map((membership) => membership.organizationName)
        .filter((name): name is string => Boolean(name));

    // `accountCount` is the API's own count and is preferred over the array length:
    // the two agree, and the field exists precisely so the screen does not count.
    const total = user.accountCount ?? names.length;
    const surplus = Math.max(0, total - 1);

    return {
        first: names[0] ?? '',
        surplus,
        // The names of the accounts folded into the « +n ». A bare « +2 » makes the
        // reader open the sheet to learn something the row already knows.
        othersTitle: surplus > 0 ? names.slice(1).join(' · ') || undefined : undefined,
    };
};

/** « plusieurs », the word an aggregate says when there is no single answer. */
export const SEVERAL = () => c('Users aggregate').t`Several`;

/**
 * What the role column shows.
 *
 * **Attention, null is not a missing value here: it is the aggregate saying « no single
 * answer ».** A membership always carries a role, so a null `roleName` means the
 * memberships diverge, and the cell reads « plusieurs ».
 *
 * **Attention, under a role filter the API switches this field** to the role that made the
 * row match, because « plusieurs » does not answer the question somebody asked by
 * filtering. Nothing is needed here for that to work: the value simply arrives
 * non-null, and no code must re-derive « plusieurs » on top of it, which would
 * contradict the very filter that is active.
 */
export const roleCell = (user: AccountUserRow): { label: string; aggregated: boolean } =>
    user.roleName ? { label: user.roleName, aggregated: false } : { label: SEVERAL(), aggregated: true };

/** Same rule as the role: null means « they diverge », never « unknown ». */
export const statusIsAggregated = (user: AccountUserRow): boolean => !user.status;

/**
 * The sentence shown when a role or status filter is on.
 *
 * **Attention, the filters mean « somewhere ».** « Porte ce rôle sur au moins un compte » is
 * not what an operator assumes while looking at a list they believe is scoped to the
 * account in front of them, and the row still enumerates *all* their accounts, not
 * only the matching ones. Said only while a filter is active: permanent, it would be
 * noise; absent, it is a misreading nobody can catch.
 */
export const somewhereNotice = (hasRoleFilter: boolean, hasStatusFilter: boolean): string | null => {
    if (hasRoleFilter && hasStatusFilter) {
        return c('Users filter notice')
            .t`These filters match a person as soon as one of their memberships qualifies. The row still lists all of their accounts, and the Role and Status columns show what made the person match.`;
    }
    if (hasRoleFilter) {
        return c('Users filter notice')
            .t`This filter matches a person who holds that role on at least one account. The row still lists all of their accounts, and the Role column shows the role that made them match.`;
    }
    if (hasStatusFilter) {
        return c('Users filter notice')
            .t`This filter matches a person who is in that state on at least one account. The row still lists all of their accounts, and the Status column shows the state that made them match.`;
    }
    return null;
};
